<script setup>
// eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import projectDocumentsAPI from 'dashboard/api/nexceo/projectDocuments';
import { useProjectHeader } from 'dashboard/composables/nexceo/useProjectHeader';
import ProjectWorkspaceLayout from './ProjectWorkspaceLayout.vue';
import { projectWorkspaceTabs } from './projectWorkspaceTabs';

const route = useRoute();
const { accountId } = useAccount();
// M29.5.16A — Approve-for-AI/customer-shareable/archive are compliance
// actions gated server-side to admin-or-team-leader (AccessPolicy
// #can_manage_document?). The frontend has no team-leader signal yet, so
// this conservatively matches only the admin half of that check — safe
// (never shows an action the backend would reject) but under-shows the
// same controls for team leaders until a `manages_project` flag is
// exposed to the frontend (follow-up, not part of this audit).
const { isAdmin } = useAdmin();
const projectId = computed(() => Number(route.params.id));

const isStandalone = computed(() => route.name === 'nexceo_project_documents');
const { project } = useProjectHeader(projectId);
// M29.5.18 — a plain agent (project_executive) is now read/download-only on
// Documents (upload/edit require manager tier or admin, see
// Nexceo::AccessPolicy#can_create_document?/#can_manage_document?).
// Defaults to "not privileged" until `project.my_role` resolves so the
// Add/Edit controls never flash visible for an agent while loading; isAdmin
// is already synchronous so an actual admin never sees that default.
const isManagerOrAdmin = computed(
  () => isAdmin.value || project.value?.my_role === 'project_manager'
);
const workspaceTabs = computed(() =>
  projectWorkspaceTabs({
    standalone: true,
    id: route.params.id,
    accountId: accountId.value,
    agentOnly: !isManagerOrAdmin.value,
  })
);

const documents = ref([]);
const isLoading = ref(false);
const error = ref(null);
const showForm = ref(false);
const editingDoc = ref(null);
const isSaving = ref(false);
const saveError = ref(null);
const saveSuccess = ref(false);
const selectedFile = ref(null);
const fileInputRef = ref(null);
// Optional cover image shown in the Sales Projects Documents grid/share
// panel instead of a generic type icon — see Nexceo::ProjectDocument
// #thumbnail. Independent of document_file: a document can be a pasted
// external_url with an uploaded thumbnail, or vice versa.
const selectedThumbnail = ref(null);
const thumbnailInputRef = ref(null);

const DOCUMENT_TYPES = [
  { value: 'brochure', label: 'Brochure' },
  { value: 'floor_plan', label: 'Floor Plan' },
  { value: 'image', label: 'Photo (Media tab)' },
  { value: 'video', label: 'Video (Media tab)' },
  { value: 'walkthrough_video', label: 'Walkthrough Video' },
  { value: 'immersive', label: 'Immersive (360°/Virtual Tour)' },
  { value: 'immersive_sample_flat', label: 'Immersive Sample Flat' },
  { value: 'price_sheet', label: 'Price Sheet' },
  { value: 'payment_plan', label: 'Payment Plan' },
  { value: 'rera', label: 'RERA' },
  { value: 'legal', label: 'Legal' },
  { value: 'amenities', label: 'Amenities' },
  { value: 'location_map', label: 'Location Map' },
  { value: 'faq', label: 'FAQ' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'other', label: 'Other' },
];

const VISIBILITIES = [
  { value: 'internal', label: 'Internal Only' },
  { value: 'ai_only', label: 'AI Only' },
  { value: 'customer_shareable', label: 'Customer Shareable' },
];

const emptyForm = () => ({
  title: '',
  document_type: 'brochure',
  file_url: '',
  external_url: '',
  description: '',
  extracted_text: '',
  visibility: 'internal',
  status: 'draft',
  approved_for_ai: false,
  customer_shareable: false,
  requires_approval: false,
});

const form = reactive(emptyForm());

async function load() {
  isLoading.value = true;
  error.value = null;
  try {
    const { data } = await projectDocumentsAPI.get(projectId.value, {
      include_archived: false,
    });
    documents.value = Array.isArray(data) ? data : [];
  } catch {
    error.value = 'Failed to load documents.';
  } finally {
    isLoading.value = false;
  }
}

function openNew() {
  editingDoc.value = null;
  Object.assign(form, emptyForm());
  showForm.value = true;
  saveError.value = null;
}

function openEdit(doc) {
  editingDoc.value = doc;
  Object.assign(form, {
    title: doc.title ?? '',
    document_type: doc.document_type ?? 'brochure',
    file_url: doc.file_url ?? '',
    external_url: doc.external_url ?? '',
    description: doc.description ?? '',
    extracted_text: doc.extracted_text ?? '',
    visibility: doc.visibility ?? 'internal',
    status: doc.status ?? 'draft',
    approved_for_ai: doc.approved_for_ai ?? false,
    customer_shareable: doc.customer_shareable ?? false,
    requires_approval: doc.requires_approval ?? false,
  });
  showForm.value = true;
  saveError.value = null;
}

function closeForm() {
  showForm.value = false;
  editingDoc.value = null;
  saveError.value = null;
  selectedFile.value = null;
  if (fileInputRef.value) fileInputRef.value.value = '';
  selectedThumbnail.value = null;
  if (thumbnailInputRef.value) thumbnailInputRef.value.value = '';
}

function onFileSelect(e) {
  selectedFile.value = e.target.files[0] || null;
}

function onThumbnailSelect(e) {
  selectedThumbnail.value = e.target.files[0] || null;
}

async function save() {
  if (!form.title.trim()) {
    saveError.value = 'Title is required.';
    return;
  }
  if (
    !selectedFile.value &&
    !form.external_url &&
    !form.file_url &&
    !form.extracted_text
  ) {
    saveError.value = 'Upload a file, provide a URL, or paste extracted text.';
    return;
  }

  isSaving.value = true;
  saveError.value = null;
  const payload = { ...form };
  const file = selectedFile.value;
  const thumbnail = selectedThumbnail.value;
  try {
    if (editingDoc.value) {
      await projectDocumentsAPI.update(
        projectId.value,
        editingDoc.value.id,
        payload,
        file,
        thumbnail
      );
    } else {
      await projectDocumentsAPI.create(
        projectId.value,
        payload,
        file,
        thumbnail
      );
    }
    await load();
    saveSuccess.value = true;
    setTimeout(() => {
      saveSuccess.value = false;
      closeForm();
    }, 2000);
  } catch (e) {
    saveError.value =
      e?.response?.data?.error ?? 'Save failed. Please try again.';
  } finally {
    isSaving.value = false;
  }
}

async function archive(doc) {
  if (!window.confirm(`Archive "${doc.title}"?`)) return;
  try {
    await projectDocumentsAPI.archive(projectId.value, doc.id);
    await load();
  } catch {
    error.value = 'Failed to archive document.';
  }
}

async function toggleAiApproval(doc) {
  try {
    if (doc.approved_for_ai) {
      await projectDocumentsAPI.revokeAiApproval(projectId.value, doc.id);
    } else {
      await projectDocumentsAPI.approveForAi(projectId.value, doc.id);
    }
    await load();
  } catch {
    error.value = 'Failed to update AI approval.';
  }
}

async function toggleCustomerShareable(doc) {
  try {
    if (doc.customer_shareable) {
      await projectDocumentsAPI.unmarkCustomerShareable(
        projectId.value,
        doc.id
      );
    } else {
      await projectDocumentsAPI.markCustomerShareable(projectId.value, doc.id);
    }
    await load();
  } catch {
    error.value = 'Failed to update shareable status.';
  }
}

function docTypeLabel(value) {
  return DOCUMENT_TYPES.find(t => t.value === value)?.label ?? value;
}

onMounted(load);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <ProjectWorkspaceLayout
    :show-chrome="isStandalone"
    :project="project"
    :tabs="workspaceTabs"
    active-tab-key="documents"
  >
    <!-- Main content -->
    <div class="flex flex-col gap-4 p-4 flex-1 overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="i-lucide-file-text size-4 text-woot-500" />
          <h2 class="text-sm font-semibold text-n-slate-12">
            Project Documents
          </h2>
          <span
            v-if="documents.length"
            class="text-xs px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-9"
          >
            {{ documents.length }}
          </span>
        </div>
        <button
          v-if="isManagerOrAdmin"
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-woot-500 text-white text-xs font-medium hover:bg-woot-600 transition-colors"
          @click="openNew"
        >
          <span class="i-lucide-plus size-3.5" />
          Add Document
        </button>
      </div>

      <!-- AI knowledge notice -->
      <div
        class="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30"
      >
        <span
          class="i-lucide-shield-alert size-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
        />
        <p class="text-xs text-amber-800 dark:text-amber-300">
          <strong>AI uses only Active + AI Approved documents.</strong>
          Documents marked Customer Shareable can be shared with leads. Internal
          and AI Only documents are never sent to customers.
        </p>
      </div>

      <!-- Error -->
      <div
        v-if="error"
        class="text-xs text-ruby-600 dark:text-ruby-400 px-3 py-2 rounded-lg bg-ruby-50 dark:bg-ruby-500/10 border border-ruby-200 dark:border-ruby-500/30"
      >
        {{ error }}
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="flex justify-center py-8">
        <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-7" />
      </div>

      <!-- Empty -->
      <div v-else-if="!documents.length && !showForm" class="text-center py-10">
        <span
          class="i-lucide-file-plus size-8 text-n-slate-6 mx-auto block mb-2"
        />
        <p class="text-sm font-medium text-n-slate-9">No documents yet.</p>
        <p class="text-xs text-n-slate-7 mt-1 max-w-xs mx-auto">
          Add brochures, floor plans, price sheets and more. The AI will use
          approved documents to answer customer questions.
        </p>
        <button
          v-if="isManagerOrAdmin"
          class="mt-4 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-woot-500 text-white text-xs font-medium hover:bg-woot-600 transition-colors mx-auto"
          @click="openNew"
        >
          <span class="i-lucide-plus size-3.5" />
          Add Document
        </button>
      </div>

      <!-- Documents list -->
      <div v-else-if="!showForm" class="flex flex-col gap-2">
        <div
          v-for="doc in documents"
          :key="doc.id"
          class="flex items-start gap-3 p-3 rounded-xl border border-n-weak bg-n-background hover:bg-n-alpha-1 transition-colors"
        >
          <!-- Icon -->
          <div
            class="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-woot-50 dark:bg-woot-500/10"
          >
            <span class="i-lucide-file-text size-4 text-woot-500" />
          </div>

          <!-- Info -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ doc.title }}
              </p>
              <span
                class="text-xs px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-9"
              >
                {{ docTypeLabel(doc.document_type) }}
              </span>
            </div>

            <!-- Description -->
            <p
              v-if="doc.description"
              class="text-xs text-n-slate-9 mt-0.5 line-clamp-1"
            >
              {{ doc.description }}
            </p>

            <!-- File link -->
            <a
              v-if="doc.document_file_url || doc.external_url"
              :href="doc.document_file_url || doc.external_url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 text-xs text-woot-600 dark:text-woot-400 hover:underline mt-0.5"
              @click.stop
            >
              <span class="i-lucide-external-link size-3" />
              {{ doc.document_file_name || 'View document' }}
            </a>

            <!-- Badges -->
            <div class="flex items-center gap-1.5 flex-wrap mt-1.5">
              <!-- Status -->
              <span
                class="text-xs px-1.5 py-0.5 rounded-full font-medium"
                :class="{
                  'bg-success-50 text-success-800 dark:bg-success-800/20 dark:text-success-400':
                    doc.status === 'active',
                  'bg-amber-50 text-amber-700 dark:bg-amber-800/20 dark:text-amber-400':
                    doc.status === 'draft',
                  'bg-n-alpha-2 text-n-slate-7': doc.status === 'archived',
                }"
              >
                {{ doc.status }}
              </span>

              <!-- AI Approved -->
              <span
                v-if="doc.approved_for_ai"
                class="text-xs px-1.5 py-0.5 rounded-full font-medium bg-woot-50 text-woot-700 dark:bg-woot-500/10 dark:text-woot-400"
              >
                <span class="i-lucide-bot size-3 inline-block mr-0.5" />
                AI Approved
              </span>

              <!-- Customer Shareable -->
              <span
                v-if="doc.customer_shareable"
                class="text-xs px-1.5 py-0.5 rounded-full font-medium bg-success-50 text-success-800 dark:bg-success-800/20 dark:text-success-400"
              >
                <span class="i-lucide-share-2 size-3 inline-block mr-0.5" />
                Customer Shareable
              </span>

              <!-- Internal only badge (when not shareable and not ai_only) -->
              <span
                v-if="doc.visibility === 'internal'"
                class="text-xs px-1.5 py-0.5 rounded-full font-medium bg-n-alpha-2 text-n-slate-8"
              >
                <span class="i-lucide-lock size-3 inline-block mr-0.5" />
                Internal Only
              </span>

              <!-- Requires Approval -->
              <span
                v-if="doc.requires_approval"
                class="text-xs px-1.5 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700 dark:bg-amber-800/20 dark:text-amber-400"
              >
                <span class="i-lucide-clock size-3 inline-block mr-0.5" />
                Requires Approval
              </span>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex items-center gap-1 shrink-0">
            <button
              v-if="isAdmin"
              :title="
                doc.approved_for_ai ? 'Revoke AI Approval' : 'Approve for AI'
              "
              class="flex items-center justify-center w-7 h-7 rounded-lg transition-colors"
              :class="
                doc.approved_for_ai
                  ? 'bg-woot-100 text-woot-600 dark:bg-woot-500/20 hover:bg-woot-200'
                  : 'text-n-slate-7 hover:bg-n-alpha-2 hover:text-woot-500'
              "
              @click="toggleAiApproval(doc)"
            >
              <span class="i-lucide-bot size-3.5" />
            </button>
            <button
              v-if="isAdmin"
              :title="
                doc.customer_shareable
                  ? 'Remove customer sharing'
                  : 'Mark customer shareable'
              "
              class="flex items-center justify-center w-7 h-7 rounded-lg transition-colors"
              :class="
                doc.customer_shareable
                  ? 'bg-success-50 text-success-700 dark:bg-success-800/20 hover:bg-success-100'
                  : 'text-n-slate-7 hover:bg-n-alpha-2 hover:text-success-600'
              "
              @click="toggleCustomerShareable(doc)"
            >
              <span class="i-lucide-share-2 size-3.5" />
            </button>
            <button
              v-if="isManagerOrAdmin"
              title="Edit"
              class="flex items-center justify-center w-7 h-7 rounded-lg text-n-slate-7 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
              @click="openEdit(doc)"
            >
              <span class="i-lucide-pencil size-3.5" />
            </button>
            <button
              v-if="isAdmin && doc.status !== 'archived'"
              title="Archive"
              class="flex items-center justify-center w-7 h-7 rounded-lg text-n-slate-7 hover:bg-ruby-50 hover:text-ruby-600 dark:hover:bg-ruby-500/10 transition-colors"
              @click="archive(doc)"
            >
              <span class="i-lucide-archive size-3.5" />
            </button>
          </div>
        </div>
      </div>

      <!-- Document Form -->
      <div
        v-if="showForm"
        class="flex flex-col gap-4 p-4 rounded-xl border border-n-weak bg-n-background"
      >
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ editingDoc ? 'Edit Document' : 'New Document' }}
          </h3>
          <button
            class="flex items-center justify-center w-7 h-7 rounded-lg text-n-slate-7 hover:bg-n-alpha-2 transition-colors"
            @click="closeForm"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Title -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Title *</label>
          <input
            v-model="form.title"
            type="text"
            placeholder="e.g. NEXCEO Heights Brochure"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
          />
        </div>

        <!-- Document Type + Status -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11"
              >Document Type *</label
            >
            <select
              v-model="form.document_type"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
            >
              <option
                v-for="t in DOCUMENT_TYPES"
                :key="t.value"
                :value="t.value"
              >
                {{ t.label }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">Status</label>
            <select
              v-model="form.status"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        <!-- Visibility -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Visibility</label>
          <select
            v-model="form.visibility"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
          >
            <option v-for="v in VISIBILITIES" :key="v.value" :value="v.value">
              {{ v.label }}
            </option>
          </select>
        </div>

        <!-- File Upload -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            Upload File
            <span class="text-n-slate-7 font-normal ml-1"
              >(PDF, images, video, etc.)</span
            >
          </label>
          <div
            class="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-1 cursor-pointer hover:border-woot-400 transition-colors"
            @click="fileInputRef?.click()"
          >
            <span class="i-lucide-upload-cloud size-4 text-n-slate-7" />
            <span class="text-sm text-n-slate-9 truncate">
              {{
                isSaving && selectedFile
                  ? 'Uploading…'
                  : selectedFile
                    ? selectedFile.name
                    : 'Click to choose a file'
              }}
            </span>
          </div>
          <input
            ref="fileInputRef"
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.webm,.m4v"
            class="hidden"
            @change="onFileSelect"
          />
          <p
            v-if="selectedFile"
            class="text-xs text-success-600 dark:text-success-400"
          >
            {{ (selectedFile.size / 1024 / 1024).toFixed(2) }} MB selected
          </p>
        </div>

        <!-- Thumbnail -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            Thumbnail Image
            <span class="text-n-slate-7 font-normal ml-1"
              >(optional — shown in Sales Projects instead of a generic icon;
              useful for PDFs/videos/immersive links)</span
            >
          </label>
          <div
            class="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-1 cursor-pointer hover:border-woot-400 transition-colors"
            @click="thumbnailInputRef?.click()"
          >
            <span class="i-lucide-image size-4 text-n-slate-7" />
            <span class="text-sm text-n-slate-9 truncate">
              {{
                isSaving && selectedThumbnail
                  ? 'Uploading…'
                  : selectedThumbnail
                    ? selectedThumbnail.name
                    : editingDoc?.thumbnail_url
                      ? 'Replace existing thumbnail'
                      : 'Click to choose an image'
              }}
            </span>
          </div>
          <input
            ref="thumbnailInputRef"
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            class="hidden"
            @change="onThumbnailSelect"
          />
          <img
            v-if="!selectedThumbnail && editingDoc?.thumbnail_url"
            :src="editingDoc.thumbnail_url"
            alt="Current thumbnail"
            class="w-20 h-20 object-cover rounded-lg border border-n-weak mt-1"
          />
        </div>

        <!-- Divider -->
        <div class="flex items-center gap-2">
          <div class="flex-1 h-px bg-n-weak" />
          <span class="text-xs text-n-slate-7">or link externally</span>
          <div class="flex-1 h-px bg-n-weak" />
        </div>

        <!-- External URL -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            External URL</label
          >
          <input
            v-model="form.external_url"
            type="url"
            placeholder="https://drive.google.com/..."
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
          />
        </div>

        <!-- Description -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">Description</label>
          <textarea
            v-model="form.description"
            rows="2"
            placeholder="Brief description of this document"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-none"
          />
        </div>

        <!-- Extracted Text -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            Extracted Text
            <span class="text-n-slate-7 font-normal ml-1"
              >(AI knowledge base)</span
            >
          </label>
          <textarea
            v-model="form.extracted_text"
            rows="5"
            placeholder="Paste key project facts, pricing, amenities, or FAQ content that the AI should know..."
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500 resize-y"
          />
          <p class="text-xs text-n-slate-7">
            This text is used by the AI to answer customer questions. Keep it
            factual and concise.
          </p>
        </div>

        <!-- Compliance fields (approved_for_ai/customer_shareable/
             requires_approval) — this whole form is now only reachable by
             isManagerOrAdmin (see Add Document/Edit buttons above), and the
             backend's create/update accept these fields directly for that
             tier (Nexceo::AccessPolicy#can_manage_document?). Still
             conservatively isAdmin-only here pending a team-leader-aware
             follow-up, per the M29.5.16A comment above. -->
        <div v-if="isAdmin" class="flex flex-col gap-2">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.approved_for_ai"
              type="checkbox"
              class="rounded border-n-weak text-woot-500 focus:ring-woot-500"
            />
            <span class="text-sm text-n-slate-12"
              >Approved for AI knowledge base</span
            >
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.customer_shareable"
              type="checkbox"
              class="rounded border-n-weak text-woot-500 focus:ring-woot-500"
            />
            <span class="text-sm text-n-slate-12"
              >Customer shareable (AI can share URL with leads)</span
            >
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="form.requires_approval"
              type="checkbox"
              class="rounded border-n-weak text-woot-500 focus:ring-woot-500"
            />
            <span class="text-sm text-n-slate-12">
              Requires human approval before sharing
            </span>
          </label>
        </div>

        <!-- AI warning -->
        <div
          class="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30"
        >
          <span
            class="i-lucide-info size-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
          />
          <p class="text-xs text-amber-800 dark:text-amber-300">
            AI replies only using approved project information. Human
            confirmation is required for booking, discount, legal, and payment
            commitments.
          </p>
        </div>

        <!-- Save Success -->
        <div
          v-if="saveSuccess"
          class="flex items-center gap-2 px-3 py-2 rounded-lg bg-success-50 dark:bg-success-800/20 border border-success-200 dark:border-success-500/20 text-success-800 dark:text-success-400 text-xs"
        >
          <span class="i-lucide-check-circle size-3.5 shrink-0" />
          Document saved.
        </div>

        <!-- Save Error -->
        <p v-if="saveError" class="text-xs text-ruby-600 dark:text-ruby-400">
          {{ saveError }}
        </p>

        <!-- Actions -->
        <div class="flex items-center gap-2 justify-end">
          <button
            class="px-4 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            @click="closeForm"
          >
            Cancel
          </button>
          <button
            :disabled="isSaving"
            class="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-woot-500 text-white font-medium hover:bg-woot-600 transition-colors disabled:opacity-50"
            @click="save"
          >
            <span
              v-if="isSaving"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            {{ editingDoc ? 'Save Changes' : 'Add Document' }}
          </button>
        </div>
      </div>
    </div>
    <!-- end main content -->
  </ProjectWorkspaceLayout>
</template>
